import React, { useState, useEffect, useRef } from "react";
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './Firebase';
import MdEditor from 'react-markdown-editor-lite';
import MarkdownIt from 'markdown-it';
import 'react-markdown-editor-lite/lib/index.css';
import * as speechsdk from 'microsoft-cognitiveservices-speech-sdk';
import { FaSpinner, FaNotesMedical, FaCheckDouble, FaClock, FaAlignJustify, FaBrain, FaConfluence, FaVolumeUp, FaNewspaper } from 'react-icons/fa';
let textToSpeak = '';
let itemModel = 'default'; // Default model

const ExplainComponent = ({ sourceDocumentID }) => {
    const [itemAnswer, setItemAnswer] = useState('');
    const [showMainApp, setShowMainApp] = useState(false);
    const [invocationType, setInvocationType] = useState('');
    const [pageTitle, setPageTitle] = useState('Explanation');
    const mdParser = new MarkdownIt(/* Markdown-it options */);
    const user = auth.currentUser;
    const speechKey = process.env.REACT_APP_AZURE_SPEECH_API_KEY;
    const serviceRegion = 'eastus';
    const voiceName = "en-US-EvelynMultilingualNeural";
    const didMountRef = useRef(false);
    const [isLiveAudioPlaying, setIsLiveAudioPlaying] = useState(false);
    const [audioUrl, setAudioUrl] = useState('');
    const audioPlayerRef = useRef(null);
    const [isPaused, setIsPaused] = useState(false);
    const isPausedRef = useRef(isPaused);
    useEffect(() => {
        fetchItemAnswer(user, sourceDocumentID);
    }, [sourceDocumentID]);
    // Helper function to split messages into chunks
    const splitMessage = (msg, chunkSize = 2000) => {
        const chunks = [];
        let currentPos = 0;
        while (currentPos < msg.length) {
            let chunk = msg.substr(currentPos, chunkSize);
            let splitPos = chunkSize;

            // If we're not at the end, look for last period
            if (currentPos + chunkSize < msg.length) {
                const lastPeriod = chunk.lastIndexOf('.');
                if (lastPeriod !== -1) {
                    splitPos = lastPeriod + 1; // Include the period
                }
                else {
                    const lastComma = chunk.lastIndexOf(',');
                    if (lastComma !== -1) {
                        splitPos = lastComma + 1; // Include the comma
                    }
                    else {
                        const lastSpace = chunk.lastIndexOf(' ');
                        if (lastSpace !== -1) {
                            splitPos = lastSpace + 1; // Include the space
                        }
                        else {
                            const lastQuestion = chunk.lastIndexOf('?');
                            if (lastQuestion !== -1) {
                                splitPos = lastSpace + 1;
                            }
                        }
                    }
                }
            }

            chunk = chunk.substr(0, splitPos);
            chunks.push(chunk.trim());
            currentPos += splitPos;
        }

        return chunks;
    };

    const synthesizeSpeech = async () => {
        setIsLiveAudioPlaying(!isLiveAudioPlaying);
        // Clean the text by removing URLs and special characters
        const cleanedArticles = textToSpeak
            .replace(/https?:\/\/[^\s]+/g, '') // Remove URLs
            .replace(/http?:\/\/[^\s]+/g, '') // Remove URLs
            .replace(/[#:\-*]/g, ' ')
            .replace(/[&]/g, ' and ')
            .replace(/[<>]/g, ' ')
            //       .replace(/["]/g, '&quot;')
            //       .replace(/[']/g, '&apos;')
            .trim(); // Remove leading/trailing spaces

        try {
            try {
                console.log('Synthesizing speech...' + cleanedArticles);
                const speechConfig = speechsdk.SpeechConfig.fromSubscription(speechKey, serviceRegion);
                speechConfig.speechSynthesisOutputFormat = speechsdk.SpeechSynthesisOutputFormat.Audio16Khz128KBitRateMonoMp3;

                speechConfig.speechSynthesisVoiceName = voiceName;
                console.log('Voice name:', voiceName);
                const audioConfig = speechsdk.AudioConfig.fromDefaultSpeakerOutput();
                const synthesizer = new speechsdk.SpeechSynthesizer(speechConfig, null); // No need to pass audioConfig here since we're capturing audio data

                // Create chunks and synthesize them sequentially
                const chunks = splitMessage(cleanedArticles);
                const audioBlobs = [];
                for (const chunk of chunks) {
                    await new Promise((resolve, reject) => {
                        synthesizer.speakTextAsync(chunk,
                            result => {
                                if (result.reason === speechsdk.ResultReason.SynthesizingAudioCompleted) {
                                    const audioData = result.audioData;
                                    const blob = new Blob([audioData], { type: 'audio/mp3' });
                                    audioBlobs.push(blob);
                                    resolve();
                                } else {
                                    reject(new Error('Synthesis failed'));
                                }
                            },
                            error => reject(error)
                        );
                    });
                }

                if (audioBlobs.length > 0) {
                    const finalBlob = new Blob(audioBlobs, { type: 'audio/mp3' });
                    setAudioUrl(URL.createObjectURL(finalBlob));
                }
            } catch (error) {
                console.error(`Error synthesizing speech: ${error}`);
            } finally {
                setIsLiveAudioPlaying(false);
            }
        }
        catch (error) {
            console.error(`Error synthesizing speech: ${error}`);
        }
        finally {
            setIsLiveAudioPlaying(false);
        }
    };

    const fetchItemAnswer = async (user, docID) => {
        try {
            const docRef = doc(db, 'genai', user.uid, 'MyGenAI', docID);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                let answerData = data.answer;
                if (answerData && typeof answerData === 'string') {
                    const thinkTag = '</think>';
                    const thinkIndex = answerData.indexOf(thinkTag);
                    if (thinkIndex !== -1) {
                        // Truncate everything before and including </think>
                        answerData = answerData.substring(thinkIndex + thinkTag.length).trim();
                    }
                }
                const invocationType = data.invocationType;
                itemModel = data.model;

                // Set page title based on invocation type
                if (invocationType === 'explain_quickly') {
                    setPageTitle('Quick Explanation');
                } else if (invocationType === 'explain_in_detail') {
                    setPageTitle('Detailed Explanation');
                } else if (invocationType === 'simple_questions') {
                    setPageTitle('Easy Questions');
                } else if (invocationType === 'difficult_questions') {
                    setPageTitle('Difficult Questions');
                } else {
                    setPageTitle('Explanation');
                }

                setInvocationType(invocationType);
                setItemAnswer(answerData);
                textToSpeak = answerData;
                console.log('Fetched explanation data:', answerData);
                console.log('Invocation type:', invocationType);
            }
        } catch (error) {
            console.error("Error fetching item answer:", error);
        }
    };

    return (
        <div className="homework-container">
            <div className="homework-container">
            <div className="button-row">
             <h2 style={{ color: 'darkBlue', margin: '0' }}>{pageTitle}</h2>
                    &nbsp;
                <button
                    className="button"
                    onClick={() => window.open('https://genai-tutor.com', '_blank')}
                >
                    GenAI Tutor
                </button>
                &nbsp;
                <button
                    className="button"
                    onClick={() => window.open('https://genai-all.com', '_blank')}
                >
                    GenAI Hub
                </button>
                &nbsp;&nbsp;&nbsp;&nbsp;
                {<button onClick={synthesizeSpeech}>                                    {isLiveAudioPlaying
                    ? (<FaSpinner className="spinning" />)
                    : (<FaVolumeUp />)}</button>}
                &nbsp;
                &nbsp;
                {audioUrl && (
                    <div>
                        <br />
                        <audio
                            ref={audioPlayerRef}
                            controls
                            style={{ width: '70%', marginLeft: '5px', marginTop: '10px' }}
                            src={audioUrl} // Add this prop
                        />
                    </div>
                )
                }
                </div>
                <div className="info-text" style={{
                    fontSize: '14px',
                    color: '#666',
                    marginTop: '5px',
                }}>
                    <span style={{ fontSize: '0.8em', color: '#888', marginLeft: '10px' }}>
                        {itemModel}
                    </span>
                    &nbsp;
                    For explanation related to geometry shapes, graphs, 3d models, please click on "GenAI All". Copy & paste question in respective tab <br />
                </div>
                <MdEditor
                    value={itemAnswer}
                    renderHTML={text => mdParser.render(text || '')}
                    readOnly={true}
                    config={{
                        view: {
                            menu: false,
                            md: false,
                            html: true
                        },
                        canView: {
                            menu: false,
                            md: false,
                            html: true,
                            fullScreen: false,
                            hideMenu: true
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default ExplainComponent;